import axios from 'axios';
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosRequestHeaders
} from 'axios';
import qs from 'qs';
import { getToken, saveToken } from './TokenManage';

// 定义你的业务响应数据结构
// 将默认的 `any` 类型替换为 `unknown`，强制在使用时进行类型检查
interface ApiResponse<T = unknown> {
  code: number;
  msg: string;
  data: T;
}

// 从环境变量获取 token
// 确保环境变量已正确配置并提供了字符串值
const TOKEN: string = import.meta.env.VITE_TOKEN as string;
const DEVELOPMENT_TOKEN: string = TOKEN;
const PRODUCTION_TOKEN: string = TOKEN;

// 设置和获取 token 的函数
// 优先从全局变量获取内存中的token，fallback到storage
const setToken = (): string | null => {
  // 1. 先尝试从全局变量获取当前token（登录过程中设置）
  if (window.__CURRENT_TOKEN__) {
    return window.__CURRENT_TOKEN__;
  }

  // 2. fallback到storage中的token
  const existingToken = getToken();
  if (existingToken) return existingToken;

  // 3. 最后使用环境变量token（仅开发环境）
  let tokenToSave;
  if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test') {
    tokenToSave = DEVELOPMENT_TOKEN;
  } else {
    tokenToSave = PRODUCTION_TOKEN;
  }
  if (tokenToSave) {
    saveToken(tokenToSave);
    return tokenToSave;
  }
  return null;
};

// 存储未完成的请求，用于取消重复请求
const pendingMap = new Map<string, AbortController>();

/**
 * 生成唯一 KEY (方法 + URL + 参数 + 数据)
 * @param config Axios 请求配置
 * @returns 唯一请求 KEY
 */
function genPendingKey(config: AxiosRequestConfig): string {
  const { method, url, params, data } = config;
  return [
    method,
    url,
    qs.stringify(params as Record<string, unknown> | undefined, {
      allowDots: true,
      arrayFormat: 'brackets'
    }),
    typeof data === 'string' ? data : data !== undefined ? JSON.stringify(data) : ''
  ].join('&');
}

/**
 *
 * @param config Axios 请求配置
 */
function addPending(config: InternalAxiosRequestConfig): void {
  const key = genPendingKey(config);
  // 检查是否已有相同请求在 pendingMap 中
  if (pendingMap.has(key)) {
    // 如果存在，取消前一个相同请求
    const controller = pendingMap.get(key);
    if (controller) {
      controller.abort();
    }
    pendingMap.delete(key);
  }
  const controller = new AbortController();
  config.signal = controller.signal;
  pendingMap.set(key, controller);
}

/**
 * 请求结束后从待处理 Map 中移除
 * @param config Axios 请求配置
 */
function removePending(config: AxiosRequestConfig): void {
  const key = genPendingKey(config);
  if (pendingMap.has(key)) {
    // 仅当请求完成或被取消时，才从 Map 中移除
    pendingMap.delete(key);
  }
}

// 创建 axios 实例
const service: AxiosInstance = axios.create({
  baseURL: (import.meta.env.VITE_BASE_API as string) || '', // 按需修改
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  },
  // GET params 序列化，支持深层对象 & 数组
  paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets', skipNulls: true })
});

console.log('===VITE_BASE_URL===', import.meta.env.VITE_BASE_URL);

/** *************************** 请求拦截 *************************** */
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // 1. 取消重复请求
    addPending(config);

    // 2. 注入鉴权 token（但排除获取token的接口）
    // getAuthToken接口使用Basic认证，不需要Bearer token
    const isAuthTokenRequest = config.url?.includes('/auth/oauth2/token');

    if (!isAuthTokenRequest) {
      const token = setToken();
      if (token) {
        // 确保 config.headers 不为 undefined
        if (!config.headers) {
          config.headers = {} as AxiosRequestHeaders;
        }
        (config.headers as AxiosRequestHeaders).Authorization = `Bearer ${token}`;
      }
    }

    // 3. GET 请求追加时间戳，避免缓存
    if (config.method === 'get') {
      config.params = {
        ...(config.params || {}),
        _t: Date.now()
      };
    }

    return config;
  },
  // 对于请求错误，AxiosError 的泛型可以设置为 unknown，表示响应数据类型未知
  (error: AxiosError<unknown>): Promise<never> => Promise.reject(error)
);

/** *************************** 响应拦截 *************************** */
service.interceptors.response.use(
  // 响应成功的类型，明确 ApiResponse 的 data 部分为 unknown
  (
    response: AxiosResponse<ApiResponse<unknown>>
  ): AxiosResponse<ApiResponse<unknown>> | Promise<never> => {
    removePending(response.config);

    const res = response.data;

    if (!Object.prototype.hasOwnProperty.call(res, 'code')) {
      return response;
    }

    if (res.code === 0) {
      return response; // 返回完整的响应，包含 data 等
    }

    // 业务错误
    // 这里可以用全局 msg 组件提示
    console.error(res.msg || '业务错误');
    return Promise.reject(new Error(res.msg || '业务错误')); // 返回业务错误信息
  },
  // 响应失败的类型，明确 AxiosError 的 data 部分为 ApiResponse<unknown>
  (error: AxiosError<ApiResponse<unknown>>): Promise<never> => {
    // 在 error 拦截器中，需要判断是否是请求配置存在，因为有些错误可能在请求发出前发生
    const { response, config } = error;
    if (config) {
      removePending(config); // 移除已完成或失败的请求
    }

    if (axios.isCancel(error)) {
      console.warn('Request canceled', error.message);
      return Promise.reject(error);
    }

    if (!response) {
      console.error('网络异常，请稍后再试');
      return Promise.reject(new Error('网络异常，请稍后再试'));
    }

    const { status, data } = response;
    const errormsg = data?.msg || `请求错误：${status}`;
    // 如果token已过期，就跳转到登陆页
    if (data && data.code === 1 && data.msg === '请求令牌已过期') {
      console.log('请求令牌已过期');
      localStorage.clear();
      const mode = import.meta.env.MODE;
      if (mode === 'development') {
        window.location.href = 'http://localhost:8000/login';
      } else if (mode === 'test') {
        window.location.href = 'https://aiot-qa.zuowei.com/login';
      } else {
        window.location.href = 'https://aiot.zuowei.com/login';
      }
      return Promise.reject(error);
    }

    switch (status) {
      case 401:
        console.warn('身份已过期，请重新登录');
        // 跳转登录页或刷新 token
        // removeToken(); // 清除旧 token
        // window.location.href = '/login';
        break;
      case 403:
        console.error('无权限访问');
        break;
      case 500:
        console.error('服务器内部错误');
        break;
      default:
        console.error(errormsg);
    }
    return Promise.reject(new Error(errormsg));
  }
);

/**
 * GET 请求封装
 * @param {string} url - 请求地址
 * @param {Record<string, unknown>} params - url query 参数
 * @param {AxiosRequestConfig} config - 额外 axios 配置
 * @returns {Promise<AxiosResponse<ApiResponse<T>>>}
 */
export function get<T = unknown>({
  url,
  params = {},
  config = {}
}: {
  url: string;
  params?: Record<string, unknown>; // 将 object 更改为 Record<string, unknown>
  config?: AxiosRequestConfig;
}): Promise<AxiosResponse<ApiResponse<T>>> {
  return service.get<ApiResponse<T>>(url, { params, ...config });
}

/**
 * POST 请求封装
 * @param {string} url - 请求地址
 * @param {Record<string, unknown> | string} data - data 参数
 * @param {AxiosRequestConfig} config - 额外 axios 配置
 * @returns {Promise<AxiosResponse<ApiResponse<T>>>}
 */
export function post<T = unknown>({
  url,
  data = {},
  config = {}
}: {
  url: string;
  data?: Record<string, unknown> | string; // 支持字符串和对象
  config?: AxiosRequestConfig;
}): Promise<AxiosResponse<ApiResponse<T>>> {
  return service.post<ApiResponse<T>>(url, data, config);
}

/**
 * PUT 请求封装
 * @param {string} url - 请求地址
 * @param {Record<string, unknown>} data - data 参数
 * @param {AxiosRequestConfig} config - 额外 axios 配置
 * @returns {Promise<AxiosResponse<ApiResponse<T>>>}
 */
export function put<T = unknown>({
  url,
  data = {},
  config = {}
}: {
  url: string;
  data?: Record<string, unknown>; // 将 object 更改为 Record<string, unknown>
  config?: AxiosRequestConfig;
}): Promise<AxiosResponse<ApiResponse<T>>> {
  return service.put<ApiResponse<T>>(url, data, config);
}

/**
 * DELETE 请求封装
 * @param {string} url - 请求地址
 * @param {Record<string, unknown>} params - url query 参数
 * @param {AxiosRequestConfig} config - 额外 axios 配置
 * @returns {Promise<AxiosResponse<ApiResponse<T>>>}
 */
export function del<T = unknown>({
  url,
  params = {},
  config = {}
}: {
  url: string;
  params?: Record<string, unknown>; // 将 object 更改为 Record<string, unknown>
  config?: AxiosRequestConfig;
}): Promise<AxiosResponse<ApiResponse<T>>> {
  return service.delete<ApiResponse<T>>(url, { params, ...config });
}

export default service;
